import React, { type ErrorInfo } from 'react';
import PropTypes from 'prop-types';
import { ErrorBoundary } from 'react-error-boundary';
import GeneralErrorState from '~go/web/src/js/components/common/error_states/general_error_state';
import Analytics from '~go/web/src/js/classes/Analytics';

type Props = {
  children: React.ReactNode;
  as: React.ElementType;
};

/**
 * This wraps any rendered JSX to give an Error Boundary
 */
export default function CustomErrorBoundary({ children, as }: Props) {
  const Component = as;

  // reload the page to be passed into the "Try again" button
  const refresh = () => window.location.reload();

  /**
   * Manually report custom boundary errors as custom events to PostHog.
   */
  const manuallyReportErrorAsEvent = (errorInfo: ErrorInfo) => {
    Analytics.track('custom_error_boundary', {
      platform: 'web',
      sent_from: 'client',
      page_location: window.location.href,
      errorInfo,
      error_happened_at: new Date(Date.now()).toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short',
      }),
    });
  };

  // Use the general error state
  return (
    <ErrorBoundary
      fallback={<Component refetch={refresh} />}
      onError={(_, errorInfo) => manuallyReportErrorAsEvent(errorInfo)}
    >
      {children}
    </ErrorBoundary>
  );
}

CustomErrorBoundary.defaultProps = {
  children: null, // Add this line to provide a default value
  as: GeneralErrorState,
};

CustomErrorBoundary.propTypes = {
  children: PropTypes.node,
  as: PropTypes.elementType,
};
